import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Box,
    Stack
} from "@mui/material/";
import { useDispatch } from "react-redux";
import actions from "../../../../../Store/actions";
import { strings } from "../../../../../common/i18n";
import styles from "../styles";
import { useAuditStatus } from "../../../../../common/hooks";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { logToSentry } from "../../../../../config/Sentry";
import { enums } from "../../../../../common/lib";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LoadingButton from "@mui/lab/LoadingButton";
import dayjs from "dayjs";

/**
 * Compact Left Layout Component
 */
export default ({
    exclusionReason,
    setExclusionReason,
    exclusionNote,
    setExclusionNote,
    setNoteType,
    onExclusionSubmit,
    exclusionDatePicker,
    handleDateChange,
    selectedDate,
    type
}) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const { typesList } = useSelector((state) => state);

    const { assetDeliveredToCustomerAndNotPaid, assetIsSold } =
        enums.status.exclusionReason;

    /** auditClose id is 6 */
    const auditCloseId = 6;
    const closedAudit = useAuditStatus().id === auditCloseId ? true : false;

    const isDisabled = useMemo(() => {
        const soldDate = new Date(selectedDate);
        const currentDate = new Date();

        if (exclusionDatePicker && exclusionNote && exclusionNote.length > 0) {
            if (exclusionReason === assetDeliveredToCustomerAndNotPaid)
                return false;
            if (exclusionReason === assetIsSold && soldDate <= currentDate)
                return false;
            if (soldDate <= currentDate) return false;
        }

        if (
            !exclusionDatePicker &&
            exclusionReason &&
            exclusionNote &&
            exclusionNote.length > 0
        ) {
            return false;
        }

        return true;
    }, [selectedDate, exclusionReason, exclusionNote, exclusionDatePicker]);

    /** Accordion Changes */
    const [expanded, setExpanded] = useState({
        panel3: type === "right" ? true : false
    });

    /** On accordion Change */
    const handleAccordionChange = (panel) => () => {
        try {
            if (panel.length > 0) {
                setExpanded((prevState) => ({
                    ...prevState,
                    [panel]: !prevState[panel]
                }));
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On accordion change:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onAccordionChange"))
            );
        }
    };

    /** Exclusion Reason Types */
    const exclusionReasonTypes = useMemo(() => {
        const { data } = typesList.exclusionReasonTypes;
        return data.length > 0 ? data : [];
    }, [typesList.exclusionReasonTypes]);

    /** On Exclusion Change */
    const handleExclusionChange = (name) => (event) => {
        try {
            const { value } = event.target;
            const type = {
                exclusionReason: () => setExclusionReason(value),
                exclusionNote: () => {
                    setNoteType("exclusion");
                    setExclusionNote(value);
                }
            };
            type[name]();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On exclusion change:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onExclusionChange"))
            );
        }
    };

    const handleExclusionSubmit = async () => {
        if (loading) return;
        try {
            setLoading(true);
            await onExclusionSubmit();
        } catch (error) {
            console.error(error);
            logToSentry("On exclusion submit:", { error });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Accordion
            sx={{
                boxShadow: "none",
                "&:before": { display: "none" },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mb: 1
            }}
            expanded={expanded.panel3}
            onChange={handleAccordionChange("panel3")}
        >
            <AccordionSummary
                expandIcon={
                    expanded.panel3 ? (
                        <RemoveIcon sx={{ fontSize: 18 }} />
                    ) : (
                        <AddIcon sx={{ fontSize: 18 }} />
                    )
                }
                sx={{
                    minHeight: 40,
                    "& .MuiAccordionSummary-content": { my: 0.5 }
                }}
            >
                <Typography variant="subtitle2" fontWeight={500}>
                    {strings("excludeAsset")}
                </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 1, pb: 2, px: 2 }}>
                <Stack spacing={1.5}>
                    {/* Exclusion Reason Dropdown */}
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: "0.875rem" }}>
                            {strings("exclusionReason")}
                        </InputLabel>
                        <Select
                            label={strings("exclusionReason")}
                            disabled={closedAudit}
                            onChange={handleExclusionChange("exclusionReason")}
                            value={exclusionReason || ""}
                            size="small"
                        >
                            {exclusionReasonTypes.map((item) => (
                                <MenuItem key={item.id} value={item.id} dense>
                                    <Typography variant="body2">
                                        {item.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Date Picker */}
                    {exclusionDatePicker && (
                        <FormControl fullWidth size="small">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    disableFuture={
                                        exclusionReason !==
                                        assetDeliveredToCustomerAndNotPaid
                                    }
                                    label={
                                        exclusionReason ===
                                        assetDeliveredToCustomerAndNotPaid
                                            ? strings("dateOfPayment")
                                            : strings("soldDate")
                                    }
                                    value={
                                        selectedDate
                                            ? dayjs(selectedDate)
                                            : null
                                    }
                                    onChange={handleDateChange}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small"
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </FormControl>
                    )}

                    {/* Note TextField */}
                    <TextField
                        fullWidth
                        multiline
                        disabled={closedAudit}
                        onChange={handleExclusionChange("exclusionNote")}
                        placeholder={strings("messages.enterNote")}
                        minRows={3}
                        maxRows={4}
                        value={exclusionNote}
                        variant="outlined"
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                fontSize: "0.875rem"
                            }
                        }}
                    />

                    {/* Submit Button */}
                    <Box sx={{ pt: 0.5 }}>
                        <LoadingButton
                            onClick={handleExclusionSubmit}
                            variant="contained"
                            loading={loading}
                            disabled={isDisabled || loading}
                            fullWidth
                            size="medium"
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                py: 0.75
                            }}
                        >
                            {strings("submit")}
                        </LoadingButton>
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
